import React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsx mdx */

import DefaultLayout from "/zeit/1c2d2ee2/node_modules/gatsby-theme-carbon/src/templates/Default.js";
import Preview from 'components/Preview';
export const _frontmatter = {};

const makeShortcode = name => function MDXDefaultShortcode(props) {
  console.warn("Component " + name + " was not imported, exported, or provided by MDXProvider as global scope");
  return <div {...props} />;
};

const AnchorLinks = makeShortcode("AnchorLinks");
const AnchorLink = makeShortcode("AnchorLink");
const InlineNotification = makeShortcode("InlineNotification");
const layoutProps = {
  _frontmatter
};
const MDXLayout = DefaultLayout;
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">



    <h3>{`This step takes our static components and populates them with data from the GitHub GraphQL API – loading states and all. We’ll be displaying Carbon repository information in a data table.`}</h3>
    <AnchorLinks mdxType="AnchorLinks">
      <AnchorLink mdxType="AnchorLink">Fork, clone and branch</AnchorLink>
      <AnchorLink mdxType="AnchorLink">Install dependencies</AnchorLink>
      <AnchorLink mdxType="AnchorLink">Create access token</AnchorLink>
      <AnchorLink mdxType="AnchorLink">Connect to Apollo</AnchorLink>
      <AnchorLink mdxType="AnchorLink">Fetch data</AnchorLink>
      <AnchorLink mdxType="AnchorLink">Populate data table</AnchorLink>
      <AnchorLink mdxType="AnchorLink">Add loading</AnchorLink>
      <AnchorLink mdxType="AnchorLink">Add pagination</AnchorLink>
      <AnchorLink mdxType="AnchorLink">Submit pull request</AnchorLink>
    </AnchorLinks>
    <h3>{`Preview`}</h3>
    <p>{`The `}<a parentName="p" {...{
        "href": "https://developer.github.com/v4/"
      }}>{`GitHub GraphQL API`}</a>{` is very well documented, and even though the focus of this tutorial isn’t learning and using GraphQL, it’s a great opportunity to fetch Carbon-related data for this Carbon tutorial.`}</p>
    <p>{`To do so, we’ll be using Apollo Client, the front-end component of the `}<a parentName="p" {...{
        "href": "https://www.apollographql.com/docs/intro/platform"
      }}>{`Apollo Platform`}</a>{`. Apollo provides several open source tools for using GraphQL throughout your application’s stack. Apollo Client is a sophisticated GraphQL client that manages data and state in an application.`}</p>
    <p>{`A `}<a parentName="p" {...{
        "href": "https://angular-step-4-carbon-tutorial.netlify.com"
      }}>{`preview`}</a>{` of what you will build (see repositories page):`}</p>
    <Preview height="400" title="Carbon Tutorial Step 3" src="https://angular-step-4-carbon-tutorial.netlify.com" frameBorder="no" allowtransparency="true" allowFullScreen="true" mdxType="Preview" />
    <InlineNotification mdxType="InlineNotification">
      <p><strong parentName="p">{`Note:`}</strong>{` If you get lint errors when you copy the code from the snippets, run `}<inlineCode parentName="p">{`ng lint --fix`}</inlineCode>{` to fix them.`}</p>
    </InlineNotification>
    <h2>{`Fork, clone and branch`}</h2>
    <p>{`This tutorial has an accompanying GitHub repository called `}<a parentName="p" {...{
        "href": "https://github.com/carbon-design-system/carbon-tutorial-angular"
      }}>{`carbon-tutorial-angular`}</a>{` that we’ll use as a starting point for each step. If you haven’t forked and cloned that repository yet, and haven’t added the upstream remote, go ahead and do so by following the `}<a parentName="p" {...{
        "href": "/tutorial/angular/step-1#fork-clone--branch"
      }}>{`step 1 instructions`}</a>{`.`}</p>
    <h3>{`Branch`}</h3>
    <p>{`With your repository all set up, let’s check out the branch for this tutorial step’s starting point.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-bash"
      }}>{`git fetch upstream
git checkout -b angular-step-3 upstream/angular-step-3
`}</code></pre>
    <h3>{`Build and start app`}</h3>
    <p>{`Install the app’s dependencies:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-bash"
      }}>{`npm install
`}</code></pre>
    <p>{`Then, start the app:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-bash"
      }}>{`npm start
`}</code></pre>
    <p>{`You should see something similar to where the `}<a parentName="p" {...{
        "href": "/tutorial/angular/step-2"
      }}>{`previous step`}</a>{` left off. Stop your app with `}<inlineCode parentName="p">{`CTRL-C`}</inlineCode>{` and let’s get everything installed.`}</p>
    <h2>{`Install dependencies`}</h2>
    <p>{`We’ll shortcut this using the Angular CLI, if you’d like more information head over to `}<a parentName="p" {...{
        "href": "https://www.apollographql.com/docs/angular/recipes/angular-cli/"
      }}>{`Angular Apollo Installation`}</a>{` for details.`}</p>
    <InlineNotification mdxType="InlineNotification">
      <p><strong parentName="p">{`Note:`}</strong>{` If you have not yet installed the Angular CLI, you will need to `}<a parentName="p" {...{
          "href": "https://cli.angular.io/"
        }}>{`install the Angular CLI`}</a>{` before running the Angular Apollo Installation.`}</p>
    </InlineNotification>
    <p>{`Install the following`}</p>
    <ul>
      <li parentName="ul"><inlineCode parentName="li">{`apollo-client`}</inlineCode>{` - package containing everything you need to set up Apollo Client`}</li>
      <li parentName="ul"><inlineCode parentName="li">{`graphql`}</inlineCode>{` - parses your GraphQL queries`}</li>
      <li parentName="ul"><inlineCode parentName="li">{`apollo-angular`}</inlineCode>{` - Apollo integration for Angular`}</li>
    </ul>
    <p>{`Using the command:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-bash"
      }}>{`ng add apollo-angular
ng lint --fix
`}</code></pre>
    <InlineNotification mdxType="InlineNotification">
      <p>{`​`}<strong parentName="p">{`Note:`}</strong>{` In case you receive `}<inlineCode parentName="p">{`ERROR in ../node_modules/graphql/subscription/subscribe.d.ts:44:3 - error TS2304: Cannot find name 'AsyncIterableIterator'.`}</inlineCode>{`, you’ll have to add the code below
in `}<inlineCode parentName="p">{`tsconfig.app.json`}</inlineCode>{` `}<inlineCode parentName="p">{`lib`}</inlineCode>{`: ​`}</p>
    </InlineNotification>
    <pre><code parentName="pre" {...{
        "className": "language-javascript",
        "metastring": "path=src/tsconfig.app.json",
        "path": "src/tsconfig.app.json"
      }}>{`"lib": [
  "es2016",
  "dom",
  "esnext.asynciterable"
],
`}</code></pre>
    <InlineNotification mdxType="InlineNotification">
      <p><strong parentName="p">{`Note:`}</strong>{` In case you receive `}<inlineCode parentName="p">{`typings.d.ts:2:13 - error TS2403: Subsequent variable declarations must have the same type. Variable 'module' must be of type 'NodeModule', but here has type '{ id: string; }'.`}</inlineCode>{`, you’ll have to comment out or remove the code below in `}<inlineCode parentName="p">{`typings.d.ts`}</inlineCode>{`:`}</p>
    </InlineNotification>
    <pre><code parentName="pre" {...{
        "className": "language-javascript",
        "metastring": "path=src/typings.d.ts",
        "path": "src/typings.d.ts"
      }}>{`declare var module: {
  id: string,
};
`}</code></pre>
    <h2>{`Create access token`}</h2>
    <p>{`You’ll need a personal access token from your GitHub account in order to make requests to the GitHub API. Check out `}<a parentName="p" {...{
        "href": "https://help.github.com/en/articles/creating-a-personal-access-token-for-the-command-line"
      }}>{`this guide`}</a>{` to see how to get one.`}</p>
    <p>{`When you get to the scope/permissions step, you can leave them all unchecked. We don’t need any special permissions, we just need access to the public API.`}</p>
    <p>{`Once you have your token, we need to put it in a place where we can use it in our app. When your application is being built and developed, our app will parse environmental variables in any `}<inlineCode parentName="p">{`environment`}</inlineCode>{` file and make them available.`}</p>
    <p>{`​`}{
        /* prettier-ignore-start */
      }</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript",
        "metastring": "path=src/environment/environment.ts",
        "path": "src/environment/environment.ts"
      }}>{`export const environment = {
  production: false,
  githubPersonalAccessToken: 'xxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx',
};
`}</code></pre>
    {
      /* prettier-ignore-end */
    }
    <p>{`Go ahead and start your app with `}<inlineCode parentName="p">{`npm start`}</inlineCode>{`, or, if your app is running, you’ll need to restart it to get access to this token.`}</p>
    <h2>{`Connect to Apollo`}</h2>
    <p>{`We need to import the environment at the top of every file that we decide to use it in:`}</p>
    {
      /* prettier-ignore-start */
    }
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`import { environment } from '../environments/environment';
`}</code></pre>
    {
      /* prettier-ignore-end */
    }
    <p>{`Next, make your client by providing a URI for the GitHub GraphQL API as well as an authorization header using the environmental variable you just added to `}<inlineCode parentName="p">{`environment.ts`}</inlineCode>{`.`}</p>
    {
      /* prettier-ignore-start */
    }
    <pre><code parentName="pre" {...{
        "className": "language-javascript",
        "metastring": "path=src/app/graphql.module.ts",
        "path": "src/app/graphql.module.ts"
      }}>{`import { HttpHeaders } from '@angular/common/http';

const uri = 'https://api.github.com/graphql'; // <-- add the URL of the GraphQL server here
export function createApollo(httpLink: HttpLink) {
  return {
    link: httpLink.create({
      uri,
      headers: new HttpHeaders({
        Authorization: \`Bearer \${environment.githubPersonalAccessToken}\`,
      }),
    }),
    cache: new InMemoryCache(),
  };
}
`}</code></pre>
    {
      /* prettier-ignore-end */
    }
    <h2>{`Fetch data`}</h2>
    <h3>{`Imports`}</h3>
    <p>{`Add the following import at the top of `}<inlineCode parentName="p">{`repo-table.component.ts`}</inlineCode>{`:`}</p>
    {
      /* prettier-ignore-start */
    }
    <pre><code parentName="pre" {...{
        "className": "language-javascript",
        "metastring": "path=src/app/repositories/repo-table/repo-table.component.ts",
        "path": "src/app/repositories/repo-table/repo-table.component.ts"
      }}>{`import { Apollo } from 'apollo-angular';
import gql from 'graphql-tag';
`}</code></pre>
    {
      /* prettier-ignore-end */
    }
    <h3>{`Query`}</h3>
    <p>{`Next we’ll assemble our GraphQL query to fetch only the data we need from the GraphQL API. We’ll do this using the `}<inlineCode parentName="p">{`gql`}</inlineCode>{` helper we just imported. The `}<inlineCode parentName="p">{`gql`}</inlineCode>{` helper lets you write GraphQL queries using interpolated strings (backticks) in JavaScript.`}</p>
    <p>{`You can use GitHub’s `}<a parentName="p" {...{
        "href": "https://developer.github.com/v4/explorer/"
      }}>{`explorer`}</a>{` tool to write and test your own queries. Try copying the query below and experiment with changing the properties. You can also click the “Docs” button in the top right of the explorer to view all of the available data and query parameters.`}</p>
    <p>{`If you’d like some more information regarding writing queries, we recommend `}<a parentName="p" {...{
        "href": "https://www.apollographql.com/docs/tutorial/queries"
      }}>{`Apollo’s documentation`}</a>{` on this topic.`}</p>
    <p>{`First, we will add a private apollo parameter of type Apollo to the constructor to get access to the Apollo service.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript",
        "metastring": "path=src/app/repositories/repo-table/repo-table.component.ts",
        "path": "src/app/repositories/repo-table/repo-table.component.ts"
      }}>{`constructor(private apollo: Apollo) { }
`}</code></pre>
    <p>{`Next, we will fetch the data in `}<inlineCode parentName="p">{`ngOnInit()`}</inlineCode>{`. Add the folowing code right below the `}<inlineCode parentName="p">{`model.header`}</inlineCode>{` declaration:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-graphql",
        "metastring": "path=src/app/repositories/repo-table/repo-table.component.ts",
        "path": "src/app/repositories/repo-table/repo-table.component.ts"
      }}>{`this.apollo.watchQuery({
  query: gql\`
    query REPO_QUERY {
      # Let's use carbon as our organization
      organization(login: "carbon-design-system") {
        # We'll grab all the repositories in one go. To load more resources
        # continuously, see the advanced topics.
        repositories(first: 75, orderBy: { field: UPDATED_AT, direction: DESC }) {
          totalCount
          nodes {
            url
            homepageUrl
            issues(filterBy: { states: OPEN }) {
              totalCount
            }
            stargazers {
              totalCount
            }
            releases(first: 1) {
              totalCount
              nodes {
                name
              }
            }
            name
            updatedAt
            createdAt
            description
            id
          }
        }
      }
    }
  \`
})
`}</code></pre>
    <h3>{`Custom data`}</h3>
    <p>{`Our last column in the data table will be a comma-separated list of repository and home page links, so let’s create a custom cell template.`}</p>
    <p>{`The column will have two values (`}<inlineCode parentName="p">{`url`}</inlineCode>{` and `}<inlineCode parentName="p">{`homepageUrl`}</inlineCode>{`) and will have the structure of an unordered list. If the repository does not have a home page URL, only render the repository link.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-html",
        "metastring": "path=src/app/repositories/repo-table/repo-table.component.html",
        "path": "src/app/repositories/repo-table/repo-table.component.html"
      }}>{`<ng-template #linkTemplate let-data="data">
  <ul style="display: flex">
    <li>
      <a ibmLink [href]="data.github">Github</a>
    </li>
    <li *ngIf="data.homepage">
      <span>&nbsp;|&nbsp;</span>
      <a ibmLink [href]="data.homepage">HomePage</a>
    </li>
  </ul>
</ng-template>
`}</code></pre>
    <pre><code parentName="pre" {...{
        "className": "language-javascript",
        "metastring": "path=src/app/repositories/repo-table/repo-table.component.ts",
        "path": "src/app/repositories/repo-table/repo-table.component.ts"
      }}>{`@ViewChild('linkTemplate', null)
protected linkTemplate: TemplateRef<any>;
`}</code></pre>
    <InlineNotification mdxType="InlineNotification">
      <p><strong parentName="p">{`Note:`}</strong>{` Don’t forget to import `}<inlineCode parentName="p">{`ViewChild`}</inlineCode>{` and `}<inlineCode parentName="p">{`TemplateRef`}</inlineCode>{`.`}</p>
    </InlineNotification>
    <p>{`Now let’s create a function that transforms row data to our expected header keys. Notice how we’re using our new `}<inlineCode parentName="p">{`linkTemplate`}</inlineCode>{` to generate the value of the `}<inlineCode parentName="p">{`links`}</inlineCode>{` key in each row.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript",
        "metastring": "path=src/app/repositories/repo-table/repo-table.component.ts",
        "path": "src/app/repositories/repo-table/repo-table.component.ts"
      }}>{`prepareData(data) {
  const newData = [];

  for (const datum of data) {
    newData.push([
      new TableItem({ data: datum.name, expandedData: datum.description }),
      new TableItem({ data: new Date(datum.createdAt).toLocaleDateString() }),
      new TableItem({ data: new Date(datum.updatedAt).toLocaleDateString() }),
      new TableItem({ data: datum.issues.totalCount }),
      new TableItem({ data: datum.stargazers.totalCount }),
      new TableItem({
        data: {
          github: datum.url,
          homepage: datum.homepageUrl
        },
        template: this.linkTemplate
      })
    ]);
  }
  return newData;
}
`}</code></pre>
    <h3>{`Query component`}</h3>
    <p>{`At this point, we should run our query and `}<inlineCode parentName="p">{`console.log()`}</inlineCode>{` the results to verify that the request is working.`}</p>
    <p>{`If there’s an issue, we’ll render the corresponding error message. We will also check when loading is true, but add the implementation in the following steps.`}</p>
    <p>{`Finally, if neither of those are true, it means we have our data! One nice advantage of GraphQL is that as long as there are no errors, we can be certain the properties on the data we requested aren’t `}<inlineCode parentName="p">{`undefined`}</inlineCode>{`.`}</p>
    <p>{`At the end of the `}<inlineCode parentName="p">{`watchQuery`}</inlineCode>{` in `}<inlineCode parentName="p">{`ngOnInit()`}</inlineCode>{`:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript",
        "metastring": "path=src/app/repositories/repo-table/repo-table.component.ts",
        "path": "src/app/repositories/repo-table/repo-table.component.ts"
      }}>{`.valueChanges.subscribe((response: any) => {
  if (response.error) {
    const errorData = [];
    errorData.push([
      new TableItem({data: 'error!' })
    ]);
    this.model.data = errorData;
  } else if (response.loading) {
    // Add loading state
  } else {
    console.log(response);
  }
});
`}</code></pre>
    <p>{`The page will look the same as we’re still rendering our static example rows, but if you view your browser’s console (e.g. `}<a parentName="p" {...{
        "href": "https://developers.google.com/web/tools/chrome-devtools/"
      }}>{`Chrome DevTools`}</a>{`), you should see the response from GitHub!`}</p>
    <h2>{`Populate data table`}</h2>
    <p>{`Now that we have that data, let’s populate the data table. Replace `}<inlineCode parentName="p">{`console.log(response);`}</inlineCode>{` with the following that destructures the `}<inlineCode parentName="p">{`organization`}</inlineCode>{` object. Once we have the repositories, we can call our `}<inlineCode parentName="p">{`prepareData()`}</inlineCode>{` to build the data table rows.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript",
        "metastring": "path=src/app/repositories/repo-table/repo-table.component.ts",
        "path": "src/app/repositories/repo-table/repo-table.component.ts"
      }}>{`// If we're here, we've got our data!
this.prepareData(response.data.organization.repositories.nodes);
`}</code></pre>
    <p>{`Then, in `}<inlineCode parentName="p">{`ngOnInit()`}</inlineCode>{` delete the static rows since we no longer need them.`}</p>
    <h2>{`Add loading`}</h2>
    <p>{`At this point, the first time that you visit the repositories page, we’re querying the GitHub API and rendering the response through the `}<inlineCode parentName="p">{`DataTable`}</inlineCode>{` component. We could stop here, but there’s more to be done! Let’s add the `}<a parentName="p" {...{
        "href": "https://angular.carbondesignsystem.com/?path=/story/table--skeleton"
      }}>{`Table Skeleton`}</a>{`.`}</p>
    <p>{`To do so, we will need to modify `}<inlineCode parentName="p">{`repo-table.component.html`}</inlineCode>{` and `}<inlineCode parentName="p">{`repo-table.component.ts`}</inlineCode>{`:`}</p>
    {
      /* prettier-ignore-start */
    }
    <pre><code parentName="pre" {...{
        "className": "language-html",
        "metastring": "path=src/app/repositories/repo-table/repo-table.component.html",
        "path": "src/app/repositories/repo-table/repo-table.component.html"
      }}>{`<ibm-table
  [skeleton]="skeleton"
  [model]="skeleton ? skeletonModel : model"
  [showSelectionColumn]="false"
  [striped]="false">
</ibm-table>
`}</code></pre>
    {
      /* prettier-ignore-end */
    }
    <p>{`We need to tell the loading skeleton how many rows to render, so let’s use 10 skeleton rows to prepare for the next enhancement…`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript",
        "metastring": "path=src/app/repositories/repo-table/repo-table.component.ts",
        "path": "src/app/repositories/repo-table/repo-table.component.ts"
      }}>{`skeletonModel = Table.skeletonModel(10, 6);
skeleton = true;
`}</code></pre>
    <InlineNotification mdxType="InlineNotification">
      <p><strong parentName="p">{`Note:`}</strong>{` Don’t forget to import `}<inlineCode parentName="p">{`Table`}</inlineCode>{`.`}</p>
    </InlineNotification>
    <p>{`Then replace the comment with:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript",
        "metastring": "path=src/app/repositories/repo-table/repo-table.component.ts",
        "path": "src/app/repositories/repo-table/repo-table.component.ts"
      }}>{`else if (response.loading) {
  this.skeleton = true;
}
`}</code></pre>
    <p>{`and at the top of the `}<inlineCode parentName="p">{`prepareData()`}</inlineCode>{` function add:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript",
        "metastring": "path=src/app/repositories/repo-table/repo-table.component.ts",
        "path": "src/app/repositories/repo-table/repo-table.component.ts"
      }}>{`this.skeleton = false;
`}</code></pre>
    <h2>{`Add pagination`}</h2>
    <p>{`Pagination! Instead of rendering every repository, let’s add pagination to the data table to only render 10 at a time. Depending on your specific requirements, you may need to fetch new data each time that you interact with the pagination component, but for simplicity, we’re going to make one request to fetch all data, and then paginate the in-memory row data.`}</p>
    <p>{`First we will create an array and populate it with 10 rows at a time.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`data = [];
`}</code></pre>
    <p>{`Next, replace the code where we call `}<inlineCode parentName="p">{`preapreData()`}</inlineCode>{` with:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript",
        "metastring": "path=src/app/repositories/repo-table/repo-table.component.ts",
        "path": "src/app/repositories/repo-table/repo-table.component.ts"
      }}>{`this.data = response.data.organization.repositories.nodes;
this.model.pageLength = 10;
this.model.totalDataLength = response.data.organization.repositories.totalCount;
this.selectPage(1);
`}</code></pre>
    <p>{`This initializes the page size to `}<inlineCode parentName="p">{`10`}</inlineCode>{` as we also specified in our loading skeleton.`}</p>
    <p>{`Next we will call `}<inlineCode parentName="p">{`selectPage()`}</inlineCode>{`. This will initialize the offset and use `}<inlineCode parentName="p">{`prepareData()`}</inlineCode>{` to only render the subset of rows for the current “page”.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript",
        "metastring": "path=src/app/repositories/repo-table/repo-table.component.ts",
        "path": "src/app/repositories/repo-table/repo-table.component.ts"
      }}>{`selectPage(page) {
  const offset = this.model.pageLength * (page - 1);
  const pageRawData = this.data.slice(offset, offset + this.model.pageLength);
  this.model.data = this.prepareData(pageRawData);
  this.model.currentPage = page;
}
`}</code></pre>
    <InlineNotification mdxType="InlineNotification">
      <p><strong parentName="p">{`Note:`}</strong>{` We only pass the rows that we want our table to display. We can do this by slicing our array of rows depending on the first item and the page size.`}</p>
    </InlineNotification>
    <p>{`Finally, let’s import the `}<inlineCode parentName="p">{`LinkModule`}</inlineCode>{` and `}<inlineCode parentName="p">{`PaginationModule`}</inlineCode>{` to `}<inlineCode parentName="p">{`repositories.module.ts`}</inlineCode>{` and `}<inlineCode parentName="p">{`repo-table.component.spec`}</inlineCode>{` and add the template for pagination.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript",
        "metastring": "path=src/app/repositories/repositories.module.ts,src/app/repositories/repo-table/repo-table.component.spec.ts",
        "path": "src/app/repositories/repositories.module.ts,src/app/repositories/repo-table/repo-table.component.spec.ts"
      }}>{`import { LinkModule, PaginationModule } from 'carbon-components-angular';
`}</code></pre>
    <pre><code parentName="pre" {...{
        "className": "language-javascript",
        "metastring": "path=src/app/repositories/repositories.module.ts,src/app/repositories/repo-table/repo-table.component.spec.ts",
        "path": "src/app/repositories/repositories.module.ts,src/app/repositories/repo-table/repo-table.component.spec.ts"
      }}>{`imports: [LinkModule, PaginationModule];
`}</code></pre>
    <p>{`Immediately after the `}<inlineCode parentName="p">{`ibm-table`}</inlineCode>{` closing tag (`}<inlineCode parentName="p">{`/>`}</inlineCode>{`), add the `}<inlineCode parentName="p">{`ibm-pagination`}</inlineCode>{` component.`}</p>
    {
      /* prettier-ignore-start */
    }
    <pre><code parentName="pre" {...{
        "className": "language-html",
        "metastring": "path=src/app/repositories/repo-table/repo-table.component.html",
        "path": "src/app/repositories/repo-table/repo-table.component.html"
      }}>{`<ibm-pagination
  [model]="model"
  (selectPage)="selectPage($event)">
</ibm-pagination>
`}</code></pre>
    {
      /* prettier-ignore-end */
    }
    <InlineNotification mdxType="InlineNotification">
      <p><strong parentName="p">{`Note:`}</strong>{` Like the other Carbon Angular component, `}<inlineCode parentName="p">{`pagination`}</inlineCode>{` component examples can be found in `}<a parentName="p" {...{
          "href": "https://angular.carbondesignsystem.com/?path=/story/table--with-pagination"
        }}>{`Storybook`}</a>{` by browsing the story and knobs.`}</p>
    </InlineNotification>
    <p>{`That does it! Your data table should fetch GitHub data on first render. You can expand each row to see the repository’s description. You can modify the pagination items per page and cycle through pages or jump to a specific page of repositories.`}</p>
    <h2>{`Submit pull request`}</h2>
    <p>{`We’re going to submit a pull request to verify completion of this tutorial step.`}</p>
    <h3>{`Continuous integration (CI) check`}</h3>
    <p>{`Run the CI check to make sure we’re all set to submit a pull request.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-bash"
      }}>{`ng lint --fix
npm run lint && npm test
`}</code></pre>
    <InlineNotification mdxType="InlineNotification">
      <p><strong parentName="p">{`Note:`}</strong>{` Having issues running the CI check? `}<a parentName="p" {...{
          "href": "/tutorial/angular/step-1#continuous-integration-(ci)-check"
        }}>{`Step 1`}</a>{` has troubleshooting notes that may help.`}</p>
    </InlineNotification>
    <h3>{`Git commit and push`}</h3>
    <p>{`Before we can create a pull request, stage and commit all of your changes:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-bash"
      }}>{`git add --all && git commit -m "feat(tutorial): complete step 3"
`}</code></pre>
    <p>{`Then, push to your repository:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-bash"
      }}>{`git push origin angular-step-3
`}</code></pre>
    <InlineNotification mdxType="InlineNotification">
      <p><strong parentName="p">{`Note:`}</strong>{` Having issues pushing your changes? `}<a parentName="p" {...{
          "href": "/tutorial/angular/step-1#git-commit-and-push"
        }}>{`Step 1`}</a>{` has troubleshooting notes that may help.`}</p>
    </InlineNotification>
    <h3>{`Pull request (PR)`}</h3>
    <p>{`Finally, visit `}<a parentName="p" {...{
        "href": "https://github.com/carbon-design-system/carbon-tutorial-angular"
      }}>{`carbon-tutorial-angular`}</a>{` to “Compare & pull request”. In doing so, make sure that you are comparing to `}<inlineCode parentName="p">{`angular-step-3`}</inlineCode>{` into `}<inlineCode parentName="p">{`base: angular-step-3`}</inlineCode>{`.`}</p>
    <InlineNotification mdxType="InlineNotification">
      <p><strong parentName="p">{`Note:`}</strong>{` Expect your tutorial step PRs to be reviewed by the Carbon team but not merged. We’ll close your PR so we can keep the repository’s remote branches pristine and ready for the next person!`}</p>
    </InlineNotification>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      